import { CompactPlan, Sample, SinglePlan } from 'prove-common'

export type PlanAchievementSample = Pick<Sample, 'takenDate'> & {
  performedCount?: number
}

type UnitCountRow = {
  id: number
  unit: string
  samplesTaken: number
  samplesPlanned: number
  name?: string
}

export type PlanBreakdownRow = {
  id: number
  planId: number
  planName: string
  samplesPlanned: number
  samplesTaken: number
}

export type OwnerBreakdownRow = {
  id: number
  unit: string
  name: string
  samplesPlanned: number
  samplesTaken: number
}

// Sorts breakdown rows so large programs/units that are falling behind rank
// first: by remaining samples (planlagt - tatt, floored at 0) descending,
// then by planned volume descending, then alphabetically by name for stability.
export const compareByRemainingThenSize = (
  a: { samplesPlanned: number; samplesTaken: number; name: string },
  b: { samplesPlanned: number; samplesTaken: number; name: string }
): number => {
  const remainingA = Math.max(a.samplesPlanned - a.samplesTaken, 0)
  const remainingB = Math.max(b.samplesPlanned - b.samplesTaken, 0)
  if (remainingA !== remainingB) return remainingB - remainingA
  if (a.samplesPlanned !== b.samplesPlanned)
    return b.samplesPlanned - a.samplesPlanned
  return a.name.localeCompare(b.name, 'nb-NO')
}

type DelegationTotals = Record<string, { taken: number; planned: number }>

export type DelegationOwnerType = 'avdeling' | 'seksjon' | 'team'

const addDelegationTotal = (
  totals: DelegationTotals,
  ownerId: string,
  taken: number,
  planned: number
) => {
  const entry = totals[ownerId] ?? { taken: 0, planned: 0 }
  entry.taken += taken
  entry.planned += planned
  totals[ownerId] = entry
}

const collectDelegationTotals = (
  plan: SinglePlan | undefined,
  ownerType: DelegationOwnerType,
  allowedOwnerIds?: Set<string>
): Record<string, { taken: number; planned: number }> => {
  if (!plan) return {}

  const totals: Record<string, { taken: number; planned: number }> = {}
  const visited = new Set<number>()

  const accumulateFromPlan = (p: SinglePlan | undefined): void => {
    if (!p || visited.has(p.id)) return
    visited.add(p.id)
    for (const task of p.tasks ?? []) {
      const delegations = task.delegations
      if (!delegations?.length) continue
      for (const d of delegations) {
        const ownerId = d.ownerId
        if (!ownerId?.startsWith(`${ownerType}:`)) continue
        if (allowedOwnerIds && !allowedOwnerIds.has(ownerId)) continue
        const entry = totals[ownerId] || { taken: 0, planned: 0 }
        entry.taken += d.totalCount ?? 0
        entry.planned += d.quantity ?? 0
        totals[ownerId] = entry
      }
    }
    for (const child of p.children ?? []) {
      accumulateFromPlan(child as unknown as SinglePlan)
    }
  }

  accumulateFromPlan(plan)
  return totals
}

export const buildPlanTotalsForOwnerType = (
  plan: SinglePlan | undefined,
  ownerType: DelegationOwnerType,
  allowedOwnerIds?: Set<string>
): { samplesTaken: number; samplesPlanned: number } => {
  const totals = collectDelegationTotals(plan, ownerType, allowedOwnerIds)

  return Object.values(totals).reduce(
    (acc, value) => ({
      samplesTaken: acc.samplesTaken + value.taken,
      samplesPlanned: acc.samplesPlanned + value.planned
    }),
    {
      samplesTaken: 0,
      samplesPlanned: 0
    }
  )
}

export const buildSamplesTakenVsPlannedForOwnerType = (
  plan: SinglePlan | undefined,
  ownerType: DelegationOwnerType,
  allowedOwnerIds?: Set<string>
): UnitCountRow[] => {
  const totals = collectDelegationTotals(plan, ownerType, allowedOwnerIds)

  return Object.entries(totals).map(([ownerId, value], idx) => ({
    id: idx,
    unit: ownerId,
    samplesTaken: value.taken,
    samplesPlanned: value.planned
  }))
}

const buildMonthLabelsForYear = (year: number) => {
  return Array.from({ length: 12 }, (_, monthIdx) => {
    const month = new Date(year, monthIdx, 1).toLocaleString('nb-NO', {
      month: 'short'
    })
    return month.charAt(0).toUpperCase() + month.slice(1)
  })
}

const distributeTotalAcrossMonths = (total: number, months: number) => {
  if (total <= 0 || months <= 0) return Array.from({ length: 12 }, () => 0)

  const base = Math.floor(total / months)
  let remainder = total % months

  return Array.from({ length: 12 }, (_, idx) => {
    if (idx >= months) return 0
    if (remainder > 0) {
      remainder -= 1
      return base + 1
    }
    return base
  })
}

const getActiveMonthsForYear = (dueDate: string | undefined, year: number) => {
  if (!dueDate) return 12

  const parsedDueDate = new Date(dueDate)
  if (parsedDueDate.getUTCFullYear() < year) return 0
  if (parsedDueDate.getUTCFullYear() > year) return 12

  let activeMonths = parsedDueDate.getUTCMonth() + 1
  if (parsedDueDate.getUTCDate() === 1) {
    activeMonths = Math.max(activeMonths - 1, 0)
  }

  return activeMonths
}

export const isPlanDueInYear = (dueDate: string | undefined, year: number) => {
  if (!dueDate) return false

  return new Date(dueDate).getUTCFullYear() === year
}

const isReportablePlanStatus = (status: CompactPlan['status']) =>
  status === 'active' || status === 'deactivated'

export const getStatisticsYears = ({
  currentYear,
  plans,
  selectedPlanDue
}: {
  currentYear: number
  plans: Array<Pick<CompactPlan, 'parents' | 'status' | 'due'>> | undefined
  selectedPlanDue?: string
}) => {
  const years = new Set<number>([currentYear])

  for (const plan of plans ?? []) {
    if (plan.parents.length > 0 || !isReportablePlanStatus(plan.status))
      continue
    const dueYear = plan.due ? new Date(plan.due).getUTCFullYear() : undefined
    if (dueYear != null && Number.isFinite(dueYear)) years.add(dueYear)
  }

  const selectedPlanYear = selectedPlanDue
    ? new Date(selectedPlanDue).getUTCFullYear()
    : undefined
  if (selectedPlanYear != null && Number.isFinite(selectedPlanYear)) {
    years.add(selectedPlanYear)
  }

  return Array.from(years).sort((a, b) => b - a)
}

const collectDelegationTotalsFromCompactPlan = (
  planId: number,
  planMap: Map<number, CompactPlan>,
  ownerType: DelegationOwnerType,
  allowedOwnerIds?: Set<string>,
  visited = new Set<number>()
): { taken: number; planned: number } => {
  if (visited.has(planId)) return { taken: 0, planned: 0 }
  visited.add(planId)

  const plan = planMap.get(planId)
  if (!plan) return { taken: 0, planned: 0 }

  let taken = 0
  let planned = 0

  for (const task of plan.tasks ?? []) {
    const delegations = task.delegations
    if (!delegations?.length) continue

    for (const delegation of delegations) {
      const ownerId = delegation.ownerId
      if (!ownerId?.startsWith(`${ownerType}:`)) continue
      if (allowedOwnerIds && !allowedOwnerIds.has(ownerId)) continue

      taken += delegation.totalCount ?? 0
      planned += delegation.quantity ?? 0
    }
  }

  for (const childId of plan.children ?? []) {
    const childTotals = collectDelegationTotalsFromCompactPlan(
      childId,
      planMap,
      ownerType,
      allowedOwnerIds,
      visited
    )
    taken += childTotals.taken
    planned += childTotals.planned
  }

  return { taken, planned }
}

const collectDelegationTotalsByOwnerFromCompactPlanRoot = (
  planId: number,
  planMap: Map<number, CompactPlan>,
  ownerType: DelegationOwnerType,
  allowedOwnerIds?: Set<string>
): DelegationTotals => {
  const totals: DelegationTotals = {}
  const visited = new Set<number>()

  const collectFromPlan = (currentPlanId: number) => {
    if (visited.has(currentPlanId)) return
    visited.add(currentPlanId)

    const plan = planMap.get(currentPlanId)
    if (!plan) return

    for (const task of plan.tasks ?? []) {
      for (const delegation of task.delegations ?? []) {
        const ownerId = delegation.ownerId
        if (!ownerId?.startsWith(`${ownerType}:`)) continue
        if (allowedOwnerIds && !allowedOwnerIds.has(ownerId)) continue

        addDelegationTotal(
          totals,
          ownerId,
          delegation.totalCount ?? 0,
          delegation.quantity ?? 0
        )
      }
    }

    for (const childId of plan.children ?? []) {
      collectFromPlan(childId)
    }
  }

  collectFromPlan(planId)
  return totals
}

const collectDelegationTotalsByOwnerFromSinglePlan = (
  plan: SinglePlan,
  ownerType: DelegationOwnerType,
  allowedOwnerIds?: Set<string>
): DelegationTotals => {
  const totals: DelegationTotals = {}
  const visited = new Set<number>()

  const collectFromPlan = (currentPlan: SinglePlan) => {
    if (visited.has(currentPlan.id)) return
    visited.add(currentPlan.id)

    for (const task of currentPlan.tasks ?? []) {
      for (const delegation of task.delegations ?? []) {
        const ownerId = delegation.ownerId
        if (!ownerId?.startsWith(`${ownerType}:`)) continue
        if (allowedOwnerIds && !allowedOwnerIds.has(ownerId)) continue

        addDelegationTotal(
          totals,
          ownerId,
          delegation.totalCount ?? 0,
          delegation.quantity ?? 0
        )
      }
    }

    for (const child of currentPlan.children ?? []) {
      collectFromPlan(child as unknown as SinglePlan)
    }
  }

  collectFromPlan(plan)
  return totals
}

export const buildPlanTotalsForPlanCollection = ({
  plans,
  year,
  ownerType,
  allowedOwnerIds
}: {
  plans: CompactPlan[] | undefined
  year: number
  ownerType: DelegationOwnerType
  allowedOwnerIds?: Set<string>
}) => {
  if (!plans?.length) {
    return {
      samplesTaken: 0,
      samplesPlanned: 0
    }
  }

  const planMap = new Map(
    plans.map((compactPlan) => [compactPlan.id, compactPlan])
  )

  return plans
    .filter(
      (compactPlan) =>
        compactPlan.parents.length === 0 &&
        isReportablePlanStatus(compactPlan.status) &&
        isPlanDueInYear(compactPlan.due, year) &&
        getActiveMonthsForYear(compactPlan.due, year) > 0
    )
    .reduce(
      (acc, compactPlan) => {
        if (!allowedOwnerIds) {
          return {
            samplesTaken: acc.samplesTaken + (compactPlan.totalCount ?? 0),
            samplesPlanned:
              acc.samplesPlanned + (compactPlan.totalQuantity ?? 0)
          }
        }

        const totals = collectDelegationTotalsFromCompactPlan(
          compactPlan.id,
          planMap,
          ownerType,
          allowedOwnerIds
        )

        return {
          samplesTaken: acc.samplesTaken + totals.taken,
          samplesPlanned: acc.samplesPlanned + totals.planned
        }
      },
      {
        samplesTaken: 0,
        samplesPlanned: 0
      }
    )
}

export const buildCalendarYearSamplesTakenVsPlanned = ({
  samples,
  plan,
  year,
  plannedSampleTotal
}: {
  samples: PlanAchievementSample[] | undefined
  plan: SinglePlan | undefined
  year: number
  plannedSampleTotal: number
}): Array<{
  id: number
  month: string
  samplesTaken: number
  samplesPlanned: number
}> => {
  const labels = buildMonthLabelsForYear(year)
  const takenCounts = Array.from({ length: 12 }, () => 0)

  for (const sample of samples ?? []) {
    if (!sample.takenDate) continue
    const date = new Date(sample.takenDate)
    if (date.getUTCFullYear() !== year) continue
    takenCounts[date.getUTCMonth()] += sample.performedCount ?? 1
  }

  const activeMonths = getActiveMonthsForYear(plan?.due, year)

  const plannedCounts = distributeTotalAcrossMonths(
    plannedSampleTotal,
    activeMonths
  )

  return labels.map((month, idx) => ({
    id: idx,
    month,
    samplesTaken: takenCounts[idx],
    samplesPlanned: plannedCounts[idx]
  }))
}

const countSamplesByMonth = (
  samples: PlanAchievementSample[] | undefined,
  year: number
): number[] => {
  const counts = Array.from({ length: 12 }, () => 0)
  for (const sample of samples ?? []) {
    if (!sample.takenDate) continue
    const date = new Date(sample.takenDate)
    if (date.getUTCFullYear() !== year) continue
    counts[date.getUTCMonth()] += sample.performedCount ?? 1
  }
  return counts
}

const isRootPlanForYear = (plan: CompactPlan, year: number): boolean =>
  plan.parents.length === 0 &&
  isReportablePlanStatus(plan.status) &&
  isPlanDueInYear(plan.due, year) &&
  getActiveMonthsForYear(plan.due, year) > 0

const accumulatePlannedCounts = (
  plans: CompactPlan[],
  year: number,
  ownerType: DelegationOwnerType,
  allowedOwnerIds?: Set<string>
): number[] => {
  const plannedCounts = Array.from({ length: 12 }, () => 0)
  const planMap = new Map(plans.map((p) => [p.id, p]))

  for (const plan of plans) {
    if (!isRootPlanForYear(plan, year)) continue

    const activeMonths = getActiveMonthsForYear(plan.due, year)
    const plannedTotal = allowedOwnerIds
      ? collectDelegationTotalsFromCompactPlan(
          plan.id,
          planMap,
          ownerType,
          allowedOwnerIds
        ).planned
      : (plan.totalQuantity ?? 0)

    const distributed = distributeTotalAcrossMonths(plannedTotal, activeMonths)
    distributed.forEach((value, monthIndex) => {
      plannedCounts[monthIndex] += value
    })
  }

  return plannedCounts
}

export const buildCalendarYearSamplesTakenVsPlannedForPlanCollection = ({
  samples,
  plans,
  plan,
  selectedPlanId,
  year,
  ownerType,
  allowedOwnerIds
}: {
  samples: PlanAchievementSample[] | undefined
  plans: CompactPlan[] | undefined
  plan?: SinglePlan
  selectedPlanId?: number
  year: number
  ownerType: DelegationOwnerType
  allowedOwnerIds?: Set<string>
}) => {
  if (selectedPlanId && plan) {
    const plannedTotal = isPlanDueInYear(plan.due, year)
      ? allowedOwnerIds
        ? buildPlanTotalsForOwnerType(plan, ownerType, allowedOwnerIds)
            .samplesPlanned
        : (plan.totalQuantity ?? 0)
      : 0

    return buildCalendarYearSamplesTakenVsPlanned({
      samples,
      plan,
      year,
      plannedSampleTotal: plannedTotal
    })
  }

  const labels = buildMonthLabelsForYear(year)
  const takenCounts = countSamplesByMonth(samples, year)
  const plannedCounts = plans?.length
    ? accumulatePlannedCounts(plans, year, ownerType, allowedOwnerIds)
    : Array.from({ length: 12 }, () => 0)

  return labels.map((month, idx) => ({
    id: idx,
    month,
    samplesTaken: takenCounts[idx],
    samplesPlanned: plannedCounts[idx]
  }))
}

export const buildSamplesPlannedTakenByTertialForPlanCollection = ({
  samples,
  plans,
  plan,
  selectedPlanId,
  year,
  ownerType,
  allowedOwnerIds
}: {
  samples: PlanAchievementSample[] | undefined
  plans: CompactPlan[] | undefined
  plan?: SinglePlan
  selectedPlanId?: number
  year: number
  ownerType: DelegationOwnerType
  allowedOwnerIds?: Set<string>
}) => {
  const months = buildCalendarYearSamplesTakenVsPlannedForPlanCollection({
    samples,
    plans,
    plan,
    selectedPlanId,
    year,
    ownerType,
    allowedOwnerIds
  })

  return ['T1', 'T2', 'T3'].map((tertial, idx) => {
    const tertialMonths = months.slice(idx * 4, idx * 4 + 4)
    return {
      id: idx,
      tertial,
      samplesPlanned: tertialMonths.reduce(
        (sum, row) => sum + row.samplesPlanned,
        0
      ),
      samplesTaken: tertialMonths.reduce(
        (sum, row) => sum + row.samplesTaken,
        0
      )
    }
  })
}

export const buildSamplesPlannedTakenByOwnerForPlanCollection = ({
  plans,
  plan,
  selectedPlanId,
  year,
  ownerType,
  allowedOwnerIds,
  ownerNames
}: {
  plans: CompactPlan[] | undefined
  plan?: SinglePlan
  selectedPlanId?: number
  year: number
  ownerType: DelegationOwnerType
  allowedOwnerIds?: Set<string>
  ownerNames?: Record<string, string>
}): OwnerBreakdownRow[] => {
  if (selectedPlanId && plan) {
    const isDueInSelectedYear = isPlanDueInYear(plan.due, year)
    return Object.entries(
      collectDelegationTotalsByOwnerFromSinglePlan(
        plan,
        ownerType,
        allowedOwnerIds
      )
    )
      .map(([ownerId, value], idx) => ({
        id: idx,
        unit: ownerId,
        name: ownerNames?.[ownerId] || ownerId,
        samplesPlanned: isDueInSelectedYear ? value.planned : 0,
        samplesTaken: value.taken
      }))
      .filter((row) => row.samplesPlanned > 0 || row.samplesTaken > 0)
      .sort(compareByRemainingThenSize)
      .map((row, idx) => ({ ...row, id: idx }))
  }

  if (!plans?.length) return []

  const planMap = new Map(plans.map((p) => [p.id, p]))
  const totals: DelegationTotals = {}

  for (const plan of plans) {
    if (!isRootPlanForYear(plan, year)) continue

    const ownerTotals = collectDelegationTotalsByOwnerFromCompactPlanRoot(
      plan.id,
      planMap,
      ownerType,
      allowedOwnerIds
    )

    for (const [ownerId, value] of Object.entries(ownerTotals)) {
      addDelegationTotal(totals, ownerId, value.taken, value.planned)
    }
  }

  return Object.entries(totals)
    .map(([ownerId, value], idx) => ({
      id: idx,
      unit: ownerId,
      name: ownerNames?.[ownerId] || ownerId,
      samplesPlanned: value.planned,
      samplesTaken: value.taken
    }))
    .filter((row) => row.samplesPlanned > 0 || row.samplesTaken > 0)
    .sort(compareByRemainingThenSize)
    .map((row, idx) => ({ ...row, id: idx }))
}

export const buildSamplesPlannedTakenByPlanCollection = ({
  plans,
  plan,
  selectedPlanId,
  year,
  ownerType,
  allowedOwnerIds,
  planNames
}: {
  plans: CompactPlan[] | undefined
  plan?: SinglePlan
  selectedPlanId?: number
  year: number
  ownerType: DelegationOwnerType
  allowedOwnerIds?: Set<string>
  planNames?: Record<number, string>
}): PlanBreakdownRow[] => {
  if (selectedPlanId && plan) {
    const ownerTotals = allowedOwnerIds
      ? buildPlanTotalsForOwnerType(plan, ownerType, allowedOwnerIds)
      : undefined

    return [
      {
        id: 0,
        planId: plan.id,
        planName: planNames?.[plan.id] ?? plan.description ?? `Plan ${plan.id}`,
        samplesPlanned: isPlanDueInYear(plan.due, year)
          ? (ownerTotals?.samplesPlanned ?? plan.totalQuantity ?? 0)
          : 0,
        samplesTaken: 0
      }
    ]
  }

  if (!plans?.length) return []

  const planMap = new Map(plans.map((p) => [p.id, p]))

  return plans
    .filter((plan) => isRootPlanForYear(plan, year))
    .map((plan, idx) => {
      const ownerTotals = allowedOwnerIds
        ? collectDelegationTotalsFromCompactPlan(
            plan.id,
            planMap,
            ownerType,
            allowedOwnerIds
          )
        : undefined
      const samplesPlanned = ownerTotals?.planned ?? plan.totalQuantity ?? 0
      const samplesTaken = 0

      return {
        id: idx,
        planId: plan.id,
        planName: planNames?.[plan.id] ?? plan.description ?? `Plan ${plan.id}`,
        samplesPlanned,
        samplesTaken
      }
    })
    .map((row, idx) => ({ ...row, id: idx }))
}
